import React, { useState } from "react";
import { ZoneBreakdownModal } from "./zoneBreakdownModal";

// Utility functions matching original
const getUtilizationColor = (percentage) => {
  if (percentage === null || percentage === 0) {
    return "rgb(230, 230, 230)";
  }
  if (percentage <= 33) {
    return "hsl(120, 100%, 40%)"; // Green
  } else if (percentage <= 66) {
    return "hsl(60, 100%, 50%)"; // Yellow
  } else if (percentage <= 95) {
    return "hsl(30, 100%, 50%)"; // Orange
  } else {
    return "hsl(0, 100%, 40%)"; // Red
  }
};

const getTextColor = (percentage) => {
  if (percentage === null || percentage === 0) {
    return "black";
  }
  if (percentage <= 33) {
    return "white";
  } else if (percentage <= 66) {
    return "black";
  } else {
    return "white";
  }
};

const getUtilizationBadgeColor = (percentage) => {
  if (percentage <= 33) return "success";
  if (percentage <= 66) return "warning";
  if (percentage <= 95) return "orange";
  return "danger";
};

const HeatmapLegend = () => (
  <div className="d-flex align-items-center gap-2">
    <div className="d-flex align-items-center">
      <div
        style={{
          height: "10px",
          width: "10px",
          backgroundColor: "rgb(230, 230, 230)",
          borderRadius: "2px",
        }}
      />
      <small className="ms-1">0%</small>
    </div>
    <div className="d-flex align-items-center">
      <div
        style={{
          height: "10px",
          width: "10px",
          backgroundColor: "hsl(120, 100%, 40%)",
          borderRadius: "2px",
        }}
      />
      <small className="ms-1">0-33%</small>
    </div>
    <div className="d-flex align-items-center">
      <div
        style={{
          height: "10px",
          width: "10px",
          backgroundColor: "hsl(60, 100%, 50%)",
          borderRadius: "2px",
        }}
      />
      <small className="ms-1">34-66%</small>
    </div>
    <div className="d-flex align-items-center">
      <div
        style={{
          height: "10px",
          width: "10px",
          backgroundColor: "hsl(30, 100%, 50%)",
          borderRadius: "2px",
        }}
      />
      <small className="ms-1">67-95%</small>
    </div>
    <div className="d-flex align-items-center">
      <div
        style={{
          height: "10px",
          width: "10px",
          backgroundColor: "hsl(0, 100%, 40%)",
          borderRadius: "2px",
        }}
      />
      <small className="ms-1">96-100%</small>
    </div>
  </div>
);

const HourDetailsModal = ({ entry, floorCapacity, onClose, onShowZoneBreakdown }) => {
  if (!entry) return null;

  const percentage = entry.percentage?.toFixed(1) || 0;
  const isOverCapacity = entry.count > floorCapacity;
  const dateTime = `${entry.hour % 12 || 12}:00 ${entry.hour >= 12 ? "PM" : "AM"}`;

  return (
    <div className="modal modal-blur fade show" style={{ display: "block" }} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{dateTime}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Utilization Details</h5>
              <span className={`badge bg-${getUtilizationBadgeColor(Number(percentage))}`}>
                {percentage}%
              </span>
            </div>
            <p className="mb-3">
              Utilization: {entry.count}/{floorCapacity}
              {isOverCapacity && (
                <span className="badge bg-danger ms-2">Over Capacity</span>
              )}
            </p>
            <hr />
            <div className="table-responsive">
              <table className="table table-sm table-vcenter">
                <thead>
                  <tr>
                    <th>Zone</th>
                    <th className="text-end">Count</th>
                    <th className="text-end">Capacity</th>
                    <th className="text-end">%</th>
                  </tr>
                </thead>
                <tbody>
                  {entry.zones
                    ?.slice()
                    .sort((a, b) => b.count - a.count)
                    .map((zone, idx) => (
                      <tr key={idx}>
                        <td>{zone.zoneName}</td>
                        <td className="text-end">{zone.count}</td>
                        <td className="text-end">{zone.capacity}</td>
                        <td className="text-end">
                          <span className={`badge bg-${getUtilizationBadgeColor(zone.percentage)}`}>
                            {zone.percentage?.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="text-center mt-3">
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={onShowZoneBreakdown}
              >
                Zone Breakdown
              </button>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const DailyHeatmap = ({ hourlyUtilizationData, floorCapacity, loading }) => {
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showZoneBreakdown, setShowZoneBreakdown] = useState(false);

  const handleHourClick = (entry) => {
    setSelectedEntry(entry);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEntry(null);
  };

  const handleShowZoneBreakdown = () => {
    setShowModal(false);
    setShowZoneBreakdown(true);
  };

  const handleCloseZoneBreakdown = () => {
    setShowZoneBreakdown(false);
    setSelectedEntry(null);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-danger" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!hourlyUtilizationData || hourlyUtilizationData.length === 0) {
    return (
      <div className="alert alert-warning">
        No utilization data available for the selected view.
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Hourly Utilization Heatmap</h3>
        <HeatmapLegend />
      </div>

      <div
        className="d-flex gap-1 justify-content-center"
        style={{
          overflowX: "auto",
          paddingBottom: "8px",
        }}
      >
        <style>
          {`
            .heatmap-scroll::-webkit-scrollbar {
              height: 6px;
            }
            .heatmap-scroll::-webkit-scrollbar-track {
              background: #f1f1f1;
              border-radius: 3px;
            }
            .heatmap-scroll::-webkit-scrollbar-thumb {
              background: #cbd5e0;
              border-radius: 3px;
            }
            .heatmap-scroll::-webkit-scrollbar-thumb:hover {
              background: #a0aec0;
            }
          `}
        </style>
        {Array.from({ length: 24 }, (_, i) => {
          const entry = hourlyUtilizationData.find((e) => e.hour === i);

          if (
            !entry ||
            entry.count === null ||
            entry.count === 0 ||
            entry.count === undefined
          ) {
            return (
              <div
                key={i}
                className="d-flex flex-column align-items-center"
                style={{ minWidth: "40px" }}
              >
                <div
                  style={{
                    height: "35px",
                    width: "35px",
                    borderRadius: "4px",
                    backgroundColor: "rgb(230, 230, 230)",
                    border: "1px solid #dee2e6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "4px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "8px",
                      fontWeight: "bold",
                      color: "#6c757d",
                    }}
                  >
                    0%
                  </span>
                </div>
                <small style={{ fontSize: "10px", color: "#6c757d" }}>
                  {`${i % 12 || 12} ${i < 12 ? "AM" : "PM"}`}
                </small>
              </div>
            );
          }

          const percentage = entry.percentage?.toFixed(1) || 0;
          const color = getUtilizationColor(percentage);
          const textColor = getTextColor(percentage);
          const isOverCapacity = entry.count > floorCapacity;

          return (
            <div
              key={i}
              className="d-flex flex-column align-items-center"
              style={{ minWidth: "40px", cursor: "pointer" }}
              onClick={() => handleHourClick(entry)}
              title={`${i}:00 - ${entry.count} people (${percentage}%)`}
            >
              <div
                style={{
                  height: "35px",
                  width: "35px",
                  borderRadius: "4px",
                  backgroundColor: color,
                  border: isOverCapacity ? "2px solid #dc3545" : "1px solid #dee2e6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "4px",
                }}
              >
                <span
                  style={{
                    fontSize: "8px",
                    fontWeight: "bold",
                    color: textColor,
                  }}
                >
                  {percentage}%
                </span>
              </div>
              <small style={{ fontSize: "10px" }}>
                {`${i % 12 || 12} ${i < 12 ? "AM" : "PM"}`}
              </small>
            </div>
          );
        })}
      </div>

      {/* Hour Details Modal */}
      {showModal && (
        <>
          <HourDetailsModal
            entry={selectedEntry}
            floorCapacity={floorCapacity}
            onClose={handleCloseModal}
            onShowZoneBreakdown={handleShowZoneBreakdown}
          />
          <div
            className="modal-backdrop fade show"
            onClick={handleCloseModal}
          ></div>
        </>
      )}

      {/* Zone Breakdown Modal */}
      {showZoneBreakdown && (
        <>
          <ZoneBreakdownModal
            entry={selectedEntry}
            floorCapacity={floorCapacity}
            onClose={handleCloseZoneBreakdown}
          />
          <div
            className="modal-backdrop fade show"
            onClick={handleCloseZoneBreakdown}
          ></div>
        </>
      )}
    </div>
  );
};
